import { useTranslation } from 'next-i18next'
import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { formatZipCodeInput } from '../utils/zipcode-formatting.utils'

import { useZipCode, useZipCodeEditModal } from './use-zipcode'

/**
 * Hook that handles all business logic for zipcode form.
 * Zipcode formatting and validation are fully config-driven: no country/locale branching.
 * Config comes from Statsig (quince-locale-info) or locale config fallback; the code only uses that config.
 */
export function useZipCodeForm() {
  const { t } = useTranslation('common')
  const { zipCode: initialZip, updateZipCode } = useZipCode()
  const { isZipcodeModalOpen: isOpen, openZipcodeModal } = useZipCodeEditModal()
  const deviceInfo = useDeviceInfo()

  const [zipcode, setZipcode] = useState<string | undefined>(initialZip ?? undefined)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  // Zipcode config from Statsig locale config (or default per locale). No country-specific logic – only this config.
  const zipcodeConfigData = useStatsigDynamicLocaleConfig()
  const zipcodeConfig = zipcodeConfigData?.zipcode
  const maxLength = zipcodeConfig?.maxLength
  const formatConfig = zipcodeConfig?.format

  // Auto-focus input on desktop when modal opens
  useEffect(() => {
    if (isDesktop && isOpen) {
      inputRef.current?.focus()
    }
    setError(null)
  }, [isDesktop, isOpen])

  // Track modal open event
  useEffect(() => {
    if (isOpen) {
      trackEvent(TELEMETRY_EVENT_NAMES.MODAL_OPEN, {
        action: 'zipcode_modal_open',
        category: 'zipCode',
        value: zipcode,
      })
    }
  }, [isOpen, zipcode])

  // Sync zipcode with initial value when modal opens
  useEffect(() => {
    if (initialZip) {
      setZipcode(initialZip)
    }
  }, [initialZip, isOpen])

  /**
   * Handles zipcode input change with formatting
   */
  const handleZipChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    const formattedValue = formatZipCodeInput(rawValue, zipcodeConfig)
    setZipcode(formattedValue)
    setError(null) // Clear error on input change
  }

  /**
   * Handles form submission
   */
  const handleSubmit = async () => {
    setError(null)
    setIsLoading(true)

    try {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      const isValid = await updateZipCode(zipcode)

      if (isValid) {
        trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
          action: 'click_submit_zipcode',
          category: 'zipCode',
          value: zipcode,
        })
        handleClose()
      } else {
        setError(t('please_enter_a_valid_zip_code'))
      }
    } catch (error) {
      // eslint-disable-next-line i18next/no-literal-string
      setError('Error setting zip code')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handles modal close
   */
  const handleClose = () => {
    openZipcodeModal(false)
  }

  return {
    // Form state
    zipcode,
    error,
    isLoading,

    // Form actions
    handleZipChange,
    handleSubmit,
    handleClose,

    // Config
    maxLength,
    formatConfig,

    // Modal state
    isOpen,
    openModal: openZipcodeModal,

    // Refs
    inputRef: inputRef,
  }
}
